import React from 'react';
import {
  Target,
  ChartBar,
  ArrowRight,
  CircleDollarSign,
  Users,
  Building2,
  GitBranch,
  Globe,
  AlertTriangle,
  CheckCircle,
  Workflow,
  Database,
  Lock,
  Network,
  Smartphone,
  BarChart2,
  Shield
} from 'lucide-react';

// Composants réutilisés (Section, AnalysisPoint, Metric)
const Section = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`mb-8 bg-white rounded-xl border border-gray-200 ${className}`}>
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center">
        <Icon className="w-6 h-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const AnalysisPoint = ({ title, status = "info", children }) => {
  const statusColors = {
    success: "border-green-500 bg-green-50",
    warning: "border-yellow-500 bg-yellow-50",
    error: "border-red-500 bg-red-50",
    info: "border-blue-500 bg-blue-50"
  };

  return (
    <div className={`border-l-4 rounded-r-lg p-4 ${statusColors[status]}`}>
      <h3 className="font-medium mb-2">{title}</h3>
      <div className="text-sm space-y-2">{children}</div>
    </div>
  );
};

const Metric = ({ label, value, trend }) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <div className="text-sm text-gray-600 mb-1">{label}</div>
    <div className="text-2xl font-semibold">{value}</div>
    {trend && (
      <div className="text-sm text-gray-500 mt-1">{trend}</div>
    )}
  </div>
);

const SmartSenegalAnalysis = () => {
  const projectContext = {
    vision: "Modernisation et digitalisation du secteur bancaire sénégalais",
    overview: {
      phase: "Phase 2 - Extension",
      timeline: "2023-2026",
      budget: "Programme pluriannuel",
      status: "40% complété"
    },
    objectives: [
      {
        title: "Infrastructure Digitale",
        items: [
          "Digital Banking Hub national",
          "APIs standardisées secteur",
          "Infrastructure Cloud dédiée"
        ]
      },
      {
        title: "Services Financiers",
        items: [
          "Adoption open banking",
          "Services digitaux innovants",
          "Inclusion financière étendue"
        ]
      },
      {
        title: "Régulation",
        items: [
          "Cadre open banking",
          "Supervision digitale",
          "Protection données"
        ]
      }
    ]
  };

  const technicalComponents = {
    core: [
      {
        name: "Digital Banking Hub",
        description: "Plateforme centralisée d'interconnexion bancaire",
        features: [
          "APIs standardisées",
          "Orchestration services",
          "Monitoring temps réel",
          "Gestion consentement"
        ],
        technologies: [
          "Microservices",
          "Event-driven",
          "OAuth 2.0",
          "OpenAPI 3.0"
        ],
        status: "En développement"
      },
      {
        name: "Open Banking Gateway",
        description: "Plateforme open banking nationale",
        features: [
          "APIs réglementaires",
          "Sandbox développeurs",
          "Monitoring usage",
          "Analytics"
        ],
        technologies: [
          "API Gateway",
          "Kafka streams",
          "GraphQL",
          "Elasticsearch"
        ],
        status: "POC"
      }
    ],
    security: {
      components: [
        "PKI nationale",
        "HSM centralisé",
        "IAM unifié",
        "Audit trail"
      ],
      standards: [
        "PCI-DSS",
        "ISO 27001",
        "NIST Framework"
      ]
    }
  };

  const implementationPlan = {
    phases: [
      {
        name: "Phase 1 - Foundation",
        period: "2023",
        completed: true,
        deliverables: [
          "Infrastructure socle",
          "APIs basiques",
          "Connectivité core"
        ]
      },
      {
        name: "Phase 2 - Extension",
        period: "2024",
        current: true,
        deliverables: [
          "Open Banking APIs",
          "Services digitaux",
          "Sandbox développeurs"
        ]
      },
      {
        name: "Phase 3 - Innovation",
        period: "2025-2026",
        deliverables: [
          "AI/ML Services",
          "Open Data",
          "Innovation hub"
        ]
      }
    ],
    keyMilestones: [
      {
        date: "Q2 2024",
        milestone: "Lancement Open Banking",
        status: "En cours"
      },
      {
        date: "Q4 2024",
        milestone: "APIs réglementaires",
        status: "Planifié"
      },
      {
        date: "2025",
        milestone: "Services innovants",
        status: "Prévu"
      }
    ]
  };

  const impacts = {
    market: [
      {
        metric: "Taux digitalisation",
        current: "25%",
        target: "75%",
        timeline: "2026"
      },
      {
        metric: "Services digitaux",
        current: "15",
        target: "50+",
        timeline: "2026"
      },
      {
        metric: "Coût services",
        current: "Base",
        target: "-40%",
        timeline: "2026"
      }
    ],
    stakeholders: [
      {
        type: "Banques",
        benefits: [
          "Réduction coûts IT",
          "Innovation accélérée",
          "Reach étendu"
        ]
      },
      {
        type: "Fintechs",
        benefits: [
          "Accès marché",
          "APIs standardisées",
          "Time-to-market réduit"
        ]
      },
      {
        type: "Clients",
        benefits: [
          "Services innovants",
          "Coûts réduits",
          "Expérience améliorée"
        ]
      }
    ]
  };

  const challenges = {
    technical: [
      {
        challenge: "Intégration systèmes legacy",
        impact: "Élevé",
        mitigation: "Architecture adaptative et APIs"
      },
      {
        challenge: "Performance et scalabilité",
        impact: "Moyen",
        mitigation: "Design cloud-native et monitoring"
      }
    ],
    operational: [
      {
        challenge: "Adoption secteur",
        impact: "Élevé",
        mitigation: "Programme accompagnement"
      },
      {
        challenge: "Gestion changement",
        impact: "Moyen",
        mitigation: "Formation et support"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Analyse Détaillée - Programme Smart Sénégal
          </h1>
          <div className="flex flex-wrap gap-4">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Phase: {projectContext.overview.phase}
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
              Progression: {projectContext.overview.status}
            </span>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
              Timeline: {projectContext.overview.timeline}
            </span>
          </div>
        </div>

        {/* Vision et Objectifs */}
        <Section title="Vision et Objectifs" icon={Target}>
          <div className="space-y-6">
            <AnalysisPoint title="Vision Stratégique" status="info">
              <p className="text-gray-700">{projectContext.vision}</p>
            </AnalysisPoint>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {projectContext.objectives.map((obj, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">{obj.title}</h3>
                  <ul className="space-y-2">
                    {obj.items.map((item, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Composants Techniques */}
        <Section title="Architecture Technique" icon={GitBranch}>
          <div className="space-y-6">
            {/* Core Components */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalComponents.core.map((component, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">{component.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      component.status === 'En développement'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {component.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{component.description}</p>
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium mb-2">Fonctionnalités:</h4>
                      <ul className="space-y-1">
                        {component.features.map((feature, i) => (
                          <li key={i} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium mb-2">Technologies:</h4>
                      <div className="flex flex-wrap gap-2">
                        {component.technologies.map((tech, i) => (
                          <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Security */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-3">Sécurité & Conformité</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Composants:</h4>
                  <ul className="space-y-1">
                    {technicalComponents.security.components.map((comp, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <Lock className="w-4 h-4 mr-2 text-blue-500" />
                        {comp}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Standards:</h4>
                  <ul className="space-y-1">
                    {technicalComponents.security.standards.map((std, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <Shield className="w-4 h-4 mr-2 text-green-500" />
                        {std}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Plan d'Implémentation */}
        <Section title="Plan d'Implémentation" icon={Workflow}>
          <div className="space-y-6">
            {/* Phases */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {implementationPlan.phases.map((phase, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">{phase.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      phase.completed 
                        ? 'bg-green-100 text-green-700'
                        : phase.current
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                    }`}>
                      {phase.completed ? 'Complété' : phase.current ? 'En cours' : 'Planifié'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Période: {phase.period}</p>
                  <ul className="space-y-1">
                    {phase.deliverables.map((deliverable, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <ArrowRight className="w-4 h-4 mr-2 text-gray-400" />
                        {deliverable}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Milestones */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-4">Milestones Clés</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {implementationPlan.keyMilestones.map((milestone, idx) => (
                  <div key={idx} className="p-3 bg-white rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">{milestone.date}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        milestone.status === 'En cours'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {milestone.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{milestone.milestone}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Impacts et Bénéfices */}
        <Section title="Impacts et Bénéfices" icon={ChartBar}>
          <div className="space-y-6">
            {/* Métriques marché */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {impacts.market.map((metric, idx) => (
                <Metric
                  key={idx}
                  label={metric.metric}
                  value={`${metric.current} → ${metric.target}`}
                  trend={`Objectif ${metric.timeline}`}
                />
              ))}
            </div>

            {/* Bénéfices par partie prenante */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {impacts.stakeholders.map((stakeholder, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">{stakeholder.type}</h3>
                  <ul className="space-y-2">
                    {stakeholder.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Défis et Risques */}
        <Section title="Défis et Risques" icon={AlertTriangle}>
          <div className="space-y-6">
            {/* Défis techniques */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <AnalysisPoint title="Défis Techniques" status="warning">
                <div className="space-y-3">
                  {challenges.technical.map((challenge, idx) => (
                    <div key={idx} className="bg-white p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{challenge.challenge}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          challenge.impact === 'Élevé'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          Impact: {challenge.impact}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Mitigation: {challenge.mitigation}
                      </p>
                    </div>
                  ))}
                </div>
              </AnalysisPoint>

              <AnalysisPoint title="Défis Opérationnels" status="warning">
                <div className="space-y-3">
                  {challenges.operational.map((challenge, idx) => (
                    <div key={idx} className="bg-white p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{challenge.challenge}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          challenge.impact === 'Élevé'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          Impact: {challenge.impact}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Mitigation: {challenge.mitigation}
                      </p>
                    </div>
                  ))}
                </div>
              </AnalysisPoint>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default SmartSenegalAnalysis;